import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Users } from "lucide-react";
import api from "@/lib/api";
import { GuideVisualCard } from "@/components/home/GuideVisualCard";
import { SiteTitle } from "@/components/home/SiteTitle";

const gridVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const PHOTO_SIZES = {
  compact: { grid: "grid-cols-3 md:grid-cols-4 xl:grid-cols-6", masonry: "columns-3 md:columns-4 xl:columns-6", aspect: "aspect-[1/1]" },
  classic: { grid: "grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5", masonry: "columns-2 md:columns-3 xl:columns-4 2xl:columns-5", aspect: "aspect-[4/5]" },
  tall: { grid: "grid-cols-2 md:grid-cols-3 xl:grid-cols-4", masonry: "columns-2 md:columns-3 xl:columns-4", aspect: "aspect-[3/4]" },
  large: { grid: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", masonry: "columns-1 sm:columns-2 lg:columns-3", aspect: "aspect-[4/5]" },
  wide: { grid: "grid-cols-1 md:grid-cols-2", masonry: "columns-1 md:columns-2", aspect: "aspect-[4/3]" },
};

export default function Guides() {
  const [guides, setGuides] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const reducedMotion = useReducedMotion();
  const effectsEnabled = content?.guides_effects_enabled !== false && !reducedMotion;
  const activeEffect = content?.guides_card_effect || "cinematic";
  const isMasonry = effectsEnabled && activeEffect === "masonry";
  const isSpotlight = effectsEnabled && activeEffect === "spotlight";
  const isTilt = effectsEnabled && activeEffect === "tilt";
  const photoSize = PHOTO_SIZES[content?.guides_photo_size] || PHOTO_SIZES.classic;

  useEffect(() => {
    Promise.all([
      api.get("/content").then(({ data }) => setContent(data)).catch(() => {}),
      api.get("/guides").then(({ data }) => setGuides(data)).catch(() => setGuides([])),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="guides-page" className="relative min-h-screen overflow-hidden bg-[#F4F1EA] text-[#1A2E26]">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F4F1EA] via-[#EDECE3] to-[#E4EAE0]" />
      <div className="absolute -z-10 top-[-12%] right-[-8%] w-[48vw] h-[48vw] rounded-full bg-[#A3B19B]/30 blur-[120px]" aria-hidden="true" />
      <div className="absolute -z-10 bottom-[-18%] left-[-10%] w-[42vw] h-[42vw] rounded-full bg-[#C86A51]/15 blur-[120px]" aria-hidden="true" />

      <div className="mx-auto max-w-7xl px-6 py-8 md:py-12">
        <header className="mb-10 md:mb-14">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Link
              to="/"
              data-testid="guides-back-home"
              className="inline-flex items-center gap-2 rounded-full border border-[#1A2E26]/15 bg-white/60 px-5 py-2.5 text-sm backdrop-blur transition-colors duration-300 hover:bg-[#1A2E26] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#C86A51]/40"
            >
              <ArrowLeft size={16} /> {content?.guides_back_label || "Back to Home"}
            </Link>
            <SiteTitle content={content} compact testId="guides-site-title" />
          </div>

          <div className="mt-14 md:mt-20 max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C86A51] mb-4">{content?.guides_page_label || "Our Guides"}</p>
            <h1 data-testid="guides-page-title" className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {content?.guides_page_title || "Thank You All Guides!!!"}
            </h1>
            <p data-testid="guides-page-message" className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[#1A2E26]/75 whitespace-pre-line">
              {content?.guides_page_message || "Here are few Guides from the first 100 batch's who took initiative and worked hard to spread Positive Life to hundreds of people and supported participants in Guides calls since 2022"}
            </p>
            {content?.guides_count_visible && (
              <p data-testid="guides-count" className="mt-5 text-sm md:text-base text-[#1A2E26]/70">
                {loading ? (content?.guides_loading_text || "Loading guides…") : `${guides.length} ${guides.length === 1 ? (content?.guides_count_singular || "guide") : (content?.guides_count_plural || "guides")}`}
              </p>
            )}
          </div>
        </header>

        {loading ? (
          <div data-testid="guides-loading" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-3xl bg-white/50 border border-white/60 p-3 animate-pulse">
                <div className="aspect-[4/5] rounded-2xl bg-[#1A2E26]/10" />
                <div className="h-5 w-2/3 mx-auto mt-4 rounded bg-[#1A2E26]/10" />
              </div>
            ))}
          </div>
        ) : guides.length > 0 ? (
          <motion.div
            variants={gridVariants}
            initial={effectsEnabled ? "hidden" : "show"}
            animate="show"
            data-testid="guides-page-grid"
            data-effect={effectsEnabled ? activeEffect : "none"}
            className={`${isMasonry ? photoSize.masonry : `grid ${photoSize.grid}`} gap-6 md:gap-8 ${isSpotlight ? "group/grid" : ""} ${isTilt ? "[perspective:1200px]" : ""} pb-16 max-h-[78vh] overflow-y-auto pr-1`}
          >
            {guides.map((guide, i) => (
              <GuideVisualCard key={guide.id} guide={guide} index={i} effect={activeEffect} effectsEnabled={effectsEnabled} aspect={isMasonry ? null : photoSize.aspect} />
            ))}
          </motion.div>
        ) : (
          <div data-testid="guides-empty" className="grid place-items-center rounded-3xl border border-white/60 bg-white/70 px-8 py-20 text-center backdrop-blur-md">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-[#C86A51]/10 text-[#C86A51] mb-5">
              <Users size={28} />
            </span>
            <h2 className="font-serif text-3xl">{content?.guides_empty_title || "Guides will appear here soon."}</h2>
            <p className="mt-3 text-sm text-[#1A2E26]/70">{content?.guides_empty_text || "Please check back later."}</p>
          </div>
        )}
      </div>
    </main>
  );
}
